// Prices from the binding Price List 1.0 (public/legal/pricing/, effective 20 October 2026).
// If the price list changes, change this file in the same commit. All prices exclude VAT.
export type Cur = 'GBP' | 'EUR';

export const PRICE_LIST_VERSION = '1.0';
export const PRICE_LIST_EFFECTIVE = '20 October 2026';

export const plans = {
  connect: { name: 'Connect', GBP: '£699', EUR: '€849', billing: 'per month, month to month' },
  scale: { name: 'Scale', GBP: '£2,500', EUR: '€3,000', billing: 'per month, annual paid monthly' },
  enterprise: { name: 'Enterprise', billing: 'By quote, per Order Form' },
};

export const partner = {
  base: { GBP: '£9,000', EUR: '€10,800', monthly: { GBP: '£750', EUR: '€900' } },
  extra: { GBP: '£2,500', EUR: '€3,000' },
  // worked example: 150 branches = base + one block of 50
  example150: { GBP: '£11,500', EUR: '€13,800' },
};

// Included allowances and the metered rate above them (Price List section 3).
export const meters: { k: string; period: string; connect: string; scale: string; then: { connect: Record<Cur, string>; scale: Record<Cur, string> }; unit: string }[] = [
  {
    k: 'Tags',
    period: 'a contract year',
    connect: '5,000',
    scale: '25,000',
    unit: 'tag',
    then: { connect: { GBP: '£0.12', EUR: '€0.15' }, scale: { GBP: '£0.10', EUR: '€0.12' } },
  },
  {
    k: 'AI resolutions',
    period: 'a month',
    connect: '250',
    scale: '1,000',
    unit: 'resolution',
    then: { connect: { GBP: '£0.69', EUR: '€0.84' }, scale: { GBP: '£0.49', EUR: '€0.59' } },
  },
  {
    k: 'AI generations',
    period: 'a month',
    connect: '300',
    scale: '1,500',
    unit: 'generation',
    then: { connect: { GBP: '£0.05', EUR: '€0.06' }, scale: { GBP: '£0.04', EUR: '€0.05' } },
  },
  {
    k: 'AI thinking',
    period: 'a month',
    connect: '500 units',
    scale: '2,000 units',
    unit: 'unit',
    then: { connect: { GBP: '£0.012', EUR: '€0.015' }, scale: { GBP: '£0.012', EUR: '€0.015' } },
  },
];

// Other lines on the price list, shown so nothing is a surprise.
export const other: { t: string; v: Record<Cur, string> }[] = [
  { t: 'Switching, data export and deletion', v: { GBP: 'Free', EUR: 'Free' } },
  { t: 'Redirect of your GS1 links and passports to a new host after you leave', v: { GBP: 'Free for 12 months', EUR: 'Free for 12 months' } },
  { t: 'Link Continuity: links and passports kept live, read-only, after you leave', v: { GBP: '£25 a month per 1,000 passports, minimum £100', EUR: '£25 a month per 1,000 passports, minimum £100' } },
  { t: 'Onboarding', v: { GBP: 'By quote, in your Order Form', EUR: 'By quote, in your Order Form' } },
  { t: 'Professional services', v: { GBP: '£1,000 per person-day', EUR: '£1,000 per person-day' } },
  { t: 'Payments you take through the platform', v: { GBP: '2% plus £0.25 per payment', EUR: '2% plus the equivalent of £0.25 per payment' } },
];

export const pricingFaqs = [
  { q: 'How does billing work?', a: 'Connect is billed monthly in advance and runs month to month. Scale is a 12-month term, billed in equal monthly instalments in advance. Metered usage above your allowance is billed monthly in arrears.' },
  { q: 'Are prices shown with or without VAT?', a: 'All prices exclude VAT and other sales taxes. EU business customers are generally billed under the reverse charge.' },
  { q: 'Can we pay in euros?', a: 'Yes. Every price is published in pounds and euros, and your Order Form records which currency you pay in.' },
  { q: 'Do you charge per user?', a: 'No. There are no per-user fees for your team, and the dealer add-on includes unlimited dealer users. It is priced by branch.' },
  { q: 'What counts as a tag?', a: 'A tag is a unique product identifier created in the platform, for example a QR code or GS1 Digital Link for one unit. Tags are not capped, because they are printed on physical products.' },
  { q: 'Can we cap AI usage?', a: 'Yes. You can set a spending cap on each AI meter. When a cap is reached, that AI feature pauses until the next period or until you raise the cap.' },
  { q: 'Who owns our data and what the AI learns?', a: 'You do. Product records, registrations, conversations and AI outputs belong to you. Your product knowledge improves only your own assistant; it never trains shared models and never answers another company\'s customers.' },
];
